import { useEffect, useState, Fragment } from "react"
import {
    Box, Paper, Typography, Button, TextField, Switch, FormControlLabel,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Backdrop, CircularProgress, Snackbar, IconButton, Chip,
    useMediaQuery, useTheme, Card, CardContent, CardActions
} from "@mui/material"
import axios from "../api/axios"
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import CloseIcon from '@mui/icons-material/Close'
import LockClockIcon from '@mui/icons-material/LockClock'

export default function CierreInversiones() {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

    const [periodos, setPeriodos] = useState([])
    const [procesando, setProcesando] = useState(false)
    const [mensaje, setMensaje] = useState("")
    const [openForm, setOpenForm] = useState(false)
    const [openConfirm, setOpenConfirm] = useState(false)
    const [idEliminar, setIdEliminar] = useState(0)

    const [form, setForm] = useState({ id: 0, nombre: "", dia_inicio: 1, dia_fin: 1, activo: true })

    useEffect(() => {
        cargarPeriodos()
    }, [])

    const cargarPeriodos = async () => {
        setProcesando(true)
        try {
            const res = await axios.get("dashboard/cierre-periodos")
            if (res.data.respuesta === "S") {
                setPeriodos(res.data.periodos || [])
            } else {
                setMensaje("Error: " + res.data.mensaje)
            }
        } catch (e) {
            setMensaje("Error de conexión")
        }
        setProcesando(false)
    }

    const abrirNuevo = () => {
        setForm({ id: 0, nombre: "", dia_inicio: 1, dia_fin: 1, activo: true })
        setOpenForm(true)
    }

    const abrirEditar = (p) => {
        setForm({ id: p.id, nombre: p.nombre, dia_inicio: p.dia_inicio, dia_fin: p.dia_fin, activo: p.activo })
        setOpenForm(true)
    }

    const guardar = async () => {
        if (!form.nombre.trim()) { setMensaje("Ingrese un nombre"); return }
        if (form.dia_inicio < 1 || form.dia_inicio > 31) { setMensaje("Día inicio debe estar entre 1 y 31"); return }
        if (form.dia_fin < 1 || form.dia_fin > 31) { setMensaje("Día fin debe estar entre 1 y 31"); return }

        setProcesando(true)
        setOpenForm(false)
        try {
            const res = await axios.post("dashboard/cierre-periodos", form)
            if (res.data.respuesta === "S") {
                setMensaje(form.id > 0 ? "Período actualizado" : "Período creado")
                cargarPeriodos()
            } else {
                setMensaje("Error: " + res.data.mensaje)
            }
        } catch (e) {
            setMensaje("Error de conexión")
        }
        setProcesando(false)
    }

    const confirmarEliminar = (id) => {
        setIdEliminar(id)
        setOpenConfirm(true)
    }

    const eliminar = async () => {
        setOpenConfirm(false)
        setProcesando(true)
        try {
            const res = await axios.delete("dashboard/cierre-periodos/" + idEliminar)
            if (res.data.respuesta === "S") {
                setMensaje("Período eliminado")
                cargarPeriodos()
            } else {
                setMensaje("Error: " + res.data.mensaje)
            }
        } catch (e) {
            setMensaje("Error de conexión")
        }
        setProcesando(false)
    }

    const descripcionRango = (inicio, fin) => {
        if (inicio <= fin) {
            return `Del ${inicio} al ${fin} de cada mes`
        }
        return `Del ${inicio} al ${fin} del mes siguiente`
    }

    const estaEnPeriodoHoy = (inicio, fin) => {
        const diaHoy = new Date().getDate()
        if (inicio <= fin) {
            return diaHoy >= inicio && diaHoy <= fin
        }
        return diaHoy >= inicio || diaHoy <= fin
    }

    const renderDesktop = () => (
        <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(10px)' }}>
            <Table size="small">
                <TableHead>
                    <TableRow sx={{ backgroundColor: 'rgba(30,58,95,0.8)' }}>
                        <TableCell sx={{ color: '#d4af37', fontWeight: 'bold' }}>Nombre</TableCell>
                        <TableCell sx={{ color: '#d4af37', fontWeight: 'bold' }} align="center">Día Inicio</TableCell>
                        <TableCell sx={{ color: '#d4af37', fontWeight: 'bold' }} align="center">Día Fin</TableCell>
                        <TableCell sx={{ color: '#d4af37', fontWeight: 'bold' }}>Rango</TableCell>
                        <TableCell sx={{ color: '#d4af37', fontWeight: 'bold' }} align="center">Estado</TableCell>
                        <TableCell sx={{ color: '#d4af37', fontWeight: 'bold' }} align="center">Hoy</TableCell>
                        <TableCell sx={{ color: '#d4af37', fontWeight: 'bold' }} align="center">Acciones</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {periodos.map(p => {
                        const hoyEnPeriodo = estaEnPeriodoHoy(p.dia_inicio, p.dia_fin)
                        return (
                            <TableRow key={p.id} sx={{
                                backgroundColor: hoyEnPeriodo && p.activo ? 'rgba(255,82,82,0.1)' : 'transparent',
                                '&:hover': { backgroundColor: 'rgba(212,175,55,0.1)' }
                            }}>
                                <TableCell sx={{ color: '#e0e0e0' }}>{p.nombre}</TableCell>
                                <TableCell sx={{ color: '#e0e0e0' }} align="center">{p.dia_inicio}</TableCell>
                                <TableCell sx={{ color: '#e0e0e0' }} align="center">{p.dia_fin}</TableCell>
                                <TableCell sx={{ color: '#b0b0b0', fontSize: '0.85rem' }}>{descripcionRango(p.dia_inicio, p.dia_fin)}</TableCell>
                                <TableCell align="center">
                                    <Chip
                                        label={p.activo ? "Activo" : "Inactivo"}
                                        size="small"
                                        sx={{
                                            backgroundColor: p.activo ? 'rgba(76,175,80,0.2)' : 'rgba(158,158,158,0.2)',
                                            color: p.activo ? '#4caf50' : '#999'
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    {hoyEnPeriodo && p.activo ? (
                                        <Chip icon={<EventBusyIcon />} label="CERRADO" size="small"
                                            sx={{ backgroundColor: 'rgba(255,82,82,0.2)', color: '#ff5252' }} />
                                    ) : (
                                        <Chip icon={<EventAvailableIcon />} label="Abierto" size="small"
                                            sx={{ backgroundColor: 'rgba(76,175,80,0.15)', color: '#4caf50' }} />
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    <IconButton size="small" onClick={() => abrirEditar(p)} sx={{ color: '#d4af37' }}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => confirmarEliminar(p.id)} sx={{ color: '#ff5252' }}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        )
                    })}
                    {periodos.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ color: '#999', py: 4 }}>
                                No hay períodos configurados
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    )

    const renderMobile = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {periodos.map(p => {
                const hoyEnPeriodo = estaEnPeriodoHoy(p.dia_inicio, p.dia_fin)
                return (
                    <Card key={p.id} sx={{
                        backgroundColor: hoyEnPeriodo && p.activo ? 'rgba(255,82,82,0.08)' : 'rgba(255,255,255,0.05)',
                        border: hoyEnPeriodo && p.activo ? '1px solid rgba(255,82,82,0.3)' : '1px solid rgba(212,175,55,0.2)'
                    }}>
                        <CardContent sx={{ pb: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography sx={{ color: '#e0e0e0', fontWeight: 'bold', fontSize: '0.95rem' }}>{p.nombre}</Typography>
                                <Chip
                                    label={p.activo ? "Activo" : "Inactivo"}
                                    size="small"
                                    sx={{
                                        backgroundColor: p.activo ? 'rgba(76,175,80,0.2)' : 'rgba(158,158,158,0.2)',
                                        color: p.activo ? '#4caf50' : '#999',
                                        height: 22
                                    }}
                                />
                            </Box>
                            <Typography sx={{ color: '#b0b0b0', fontSize: '0.85rem', mb: 0.5 }}>
                                Días: {p.dia_inicio} - {p.dia_fin}
                            </Typography>
                            <Typography sx={{ color: '#999', fontSize: '0.8rem' }}>
                                {descripcionRango(p.dia_inicio, p.dia_fin)}
                            </Typography>
                            {hoyEnPeriodo && p.activo && (
                                <Chip icon={<EventBusyIcon />} label="HOY CERRADO" size="small"
                                    sx={{ mt: 1, backgroundColor: 'rgba(255,82,82,0.2)', color: '#ff5252' }} />
                            )}
                        </CardContent>
                        <CardActions sx={{ pt: 0, justifyContent: 'flex-end' }}>
                            <Button size="small" onClick={() => abrirEditar(p)} sx={{ color: '#d4af37' }}>Editar</Button>
                            <Button size="small" onClick={() => confirmarEliminar(p.id)} sx={{ color: '#ff5252' }}>Eliminar</Button>
                        </CardActions>
                    </Card>
                )
            })}
            {periodos.length === 0 && (
                <Typography align="center" sx={{ color: '#999', py: 4 }}>No hay períodos configurados</Typography>
            )}
        </Box>
    )

    return (
        <Box sx={{ p: isMobile ? 1.5 : 3, maxWidth: 900, margin: '0 auto' }}>
            {/* Header */}
            <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                mb: 3, flexWrap: 'wrap', gap: 1
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LockClockIcon sx={{ color: '#d4af37', fontSize: isMobile ? 28 : 32 }} />
                    <Typography variant={isMobile ? "h6" : "h5"} sx={{ color: '#e0e0e0', fontWeight: 'bold' }}>
                        Cierre de Inversiones
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={abrirNuevo}
                    size={isMobile ? "small" : "medium"}
                    sx={{
                        backgroundColor: '#d4af37', color: '#1e3a5f', fontWeight: 'bold',
                        '&:hover': { backgroundColor: '#c9a030' }
                    }}
                >
                    Nuevo Período
                </Button>
            </Box>

            {/* Info */}
            <Paper sx={{
                p: 2, mb: 3, backgroundColor: 'rgba(30,58,95,0.4)',
                border: '1px solid rgba(212,175,55,0.2)'
            }}>
                <Typography sx={{ color: '#b0b0b0', fontSize: '0.85rem' }}>
                    Configure los rangos de días del mes en que las inversiones estarán cerradas.
                    Si el día inicio es mayor que el día fin, el período cruza al mes siguiente (ej: del 27 al 3).
                </Typography>
            </Paper>

            {/* Table / Cards */}
            {isMobile ? renderMobile() : renderDesktop()}

            {/* Form Dialog */}
            <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="xs" fullWidth
                PaperProps={{ sx: { backgroundColor: '#1e3a5f', color: '#e0e0e0' } }}>
                <DialogTitle sx={{ color: '#d4af37' }}>
                    {form.id > 0 ? "Editar Período" : "Nuevo Período"}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        label="Nombre" fullWidth margin="dense" size="small"
                        value={form.nombre}
                        onChange={e => setForm({ ...form, nombre: e.target.value })}
                        sx={{ mb: 2, '& .MuiInputBase-input': { color: '#e0e0e0' }, '& .MuiInputLabel-root': { color: '#999' },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(212,175,55,0.3)' } }}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="Día Inicio" type="number" size="small"
                            value={form.dia_inicio}
                            onChange={e => setForm({ ...form, dia_inicio: parseInt(e.target.value) || 1 })}
                            inputProps={{ min: 1, max: 31 }}
                            sx={{ flex: 1, '& .MuiInputBase-input': { color: '#e0e0e0' }, '& .MuiInputLabel-root': { color: '#999' },
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(212,175,55,0.3)' } }}
                        />
                        <TextField
                            label="Día Fin" type="number" size="small"
                            value={form.dia_fin}
                            onChange={e => setForm({ ...form, dia_fin: parseInt(e.target.value) || 1 })}
                            inputProps={{ min: 1, max: 31 }}
                            sx={{ flex: 1, '& .MuiInputBase-input': { color: '#e0e0e0' }, '& .MuiInputLabel-root': { color: '#999' },
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(212,175,55,0.3)' } }}
                        />
                    </Box>
                    <FormControlLabel
                        sx={{ mt: 2, color: '#e0e0e0' }}
                        control={<Switch checked={form.activo} onChange={e => setForm({ ...form, activo: e.target.checked })}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#d4af37' },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#d4af37' } }} />}
                        label="Activo"
                    />
                    {form.dia_inicio > 0 && form.dia_fin > 0 && (
                        <Typography sx={{ mt: 1, color: '#999', fontSize: '0.8rem' }}>
                            {descripcionRango(form.dia_inicio, form.dia_fin)}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenForm(false)} sx={{ color: '#999' }}>Cancelar</Button>
                    <Button onClick={guardar} sx={{ color: '#d4af37', fontWeight: 'bold' }}>Guardar</Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Delete Dialog */}
            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}
                PaperProps={{ sx: { backgroundColor: '#1e3a5f', color: '#e0e0e0' } }}>
                <DialogTitle sx={{ color: '#ff5252' }}>Confirmar Eliminación</DialogTitle>
                <DialogContent>
                    <Typography>¿Está seguro de eliminar este período de cierre?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirm(false)} sx={{ color: '#999' }}>Cancelar</Button>
                    <Button onClick={eliminar} sx={{ color: '#ff5252', fontWeight: 'bold' }}>Eliminar</Button>
                </DialogActions>
            </Dialog>

            {/* Loading */}
            <Backdrop open={procesando} sx={{ zIndex: 9999 }}>
                <CircularProgress sx={{ color: '#d4af37' }} />
            </Backdrop>

            {/* Snackbar */}
            <Snackbar
                open={mensaje !== ""}
                autoHideDuration={4000}
                onClose={() => setMensaje("")}
                message={mensaje}
                action={
                    <IconButton size="small" color="inherit" onClick={() => setMensaje("")}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                }
            />
        </Box>
    )
}
